// packages/ops-platform-v3/src/frontend/expoams/InventoryView.tsx
//
// Ported from StationJo's src/components/expoams/InventoryView.tsx (Plan 09 Task 10).
// - Inventory reads (getLocationInventory, getMasterInventory) go through the
//   ExpoAmsClient singleton from ./services/index.js (Plan 09 Task 15).
// - Types import swapped from '../../types/expoams' to './types.js' (siblings).
// - No Firebase Web SDK imports.
// - `noUncheckedIndexedAccess` is satisfied via local variable caching when
//   dereferencing sheetNames[id].

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { LocationInventory, MasterInventoryItem } from './types.js';
import { expoamsClient } from './services/index.js';

// Thin wrappers that strip the envelope shape returned by ExpoAmsClient so
// the existing render logic can keep working with plain arrays.
const fetchLocationInventory = async (sheetId: string): Promise<LocationInventory[]> => {
  const { locations } = await expoamsClient.getLocationInventory({ sheetId });
  return locations;
};
const fetchMasterInventory = async (sheetId: string): Promise<MasterInventoryItem[]> => {
  const { items } = await expoamsClient.getMasterInventory({ sheetId });
  return items;
};

export interface InventoryViewProps {
  sheetIds: string[];
  sheetNames: Record<string, string>;
  selectedSheetId: string;
  onSelectSheet: (id: string) => void;
  /** Bumped by the router after a scan; invalidates every cached read. */
  dataVersion?: number;
}

type ViewMode = 'by-location' | 'by-item';

// Per-view response cache. Toggling By Item <-> By Location used to refetch
// from Google Sheets each time (two quota-counted reads per toggle); now a
// sheet is read once per view until the router bumps `dataVersion` (after a
// scan) or the user presses Refresh.
interface CachedInventory {
  locations?: LocationInventory[];
  items?: MasterInventoryItem[];
}

export const InventoryView: React.FC<InventoryViewProps> = ({
  sheetIds,
  sheetNames,
  selectedSheetId,
  onSelectSheet,
  dataVersion = 0,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('by-item');
  const [locations, setLocations] = useState<LocationInventory[]>([]);
  const [items, setItems] = useState<MasterInventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const cacheRef = useRef<Map<string, CachedInventory>>(new Map());
  const cacheVersionRef = useRef(dataVersion);

  if (cacheVersionRef.current !== dataVersion) {
    cacheVersionRef.current = dataVersion;
    cacheRef.current.clear();
  }

  const refresh = useCallback(() => {
    cacheRef.current.delete(selectedSheetId);
    setRefreshNonce((n) => n + 1);
  }, [selectedSheetId]);

  useEffect(() => {
    if (!selectedSheetId) return;
    const cached = cacheRef.current.get(selectedSheetId) ?? {};
    if (viewMode === 'by-location' && cached.locations) {
      setLocations(cached.locations);
      setError(null);
      return;
    }
    if (viewMode === 'by-item' && cached.items) {
      setItems(cached.items);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetcher =
      viewMode === 'by-location'
        ? fetchLocationInventory(selectedSheetId).then((locs) => {
            if (cancelled) return;
            cacheRef.current.set(selectedSheetId, { ...cached, locations: locs });
            setLocations(locs);
          })
        : fetchMasterInventory(selectedSheetId).then((its) => {
            if (cancelled) return;
            cacheRef.current.set(selectedSheetId, { ...cached, items: its });
            setItems(its);
          });
    fetcher
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSheetId, viewMode, refreshNonce, dataVersion]);

  const filteredLocations = locations.filter(
    (loc) =>
      loc.location.toLowerCase().includes(search.toLowerCase()) ||
      loc.items.some((i) => i.identifier.toLowerCase().includes(search.toLowerCase())),
  );

  const filteredItems = items.filter((item) =>
    item.identifier.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      <select
        className="expoams-sheet-select"
        value={selectedSheetId}
        onChange={(e) => onSelectSheet(e.target.value)}
        style={{ marginBottom: '0.75rem' }}
      >
        <option value="" disabled>
          — Select a sheet —
        </option>
        {sheetIds.map((id) => {
          // noUncheckedIndexedAccess: cache sheetNames[id] locally so the
          // resolved label is narrowed to string (fallback to id).
          const label = sheetNames[id] ?? id;
          return (
            <option key={id} value={id}>
              {label}
            </option>
          );
        })}
      </select>

      {!selectedSheetId && (
        <div style={{ textAlign: 'center', color: '#888', margin: '1rem 0', fontSize: '0.9rem' }}>
          Select a sheet above to view inventory.
        </div>
      )}

      {/* View mode toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button
          className={`expoams-purpose-btn ${viewMode === 'by-location' ? 'selected' : ''}`}
          style={{ flex: 1, padding: '0.5rem' }}
          onClick={() => setViewMode('by-location')}
        >
          By Location
        </button>
        <button
          className={`expoams-purpose-btn ${viewMode === 'by-item' ? 'selected' : ''}`}
          style={{ flex: 1, padding: '0.5rem' }}
          onClick={() => setViewMode('by-item')}
        >
          By Item
        </button>
      </div>

      {/* Search + refresh */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <input
          className="expoams-add-input"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="expoams-purpose-btn"
          onClick={refresh}
          disabled={!selectedSheetId || loading}
          aria-label="Refresh inventory"
          title="Re-read this sheet from Google Sheets"
        >
          ↻ Refresh
        </button>
      </div>

      {error && <div className="expoams-error">{error}</div>}
      {loading && <div className="expoams-loading">Loading...</div>}

      {/* By Location */}
      {!loading &&
        viewMode === 'by-location' &&
        filteredLocations.map((loc) => (
          <div key={loc.location} className="expoams-history-card">
            <div className="expoams-history-card-header">
              <span>📍 {loc.location}</span>
              <span style={{ color: '#999', fontSize: '0.85rem' }}>{loc.items.length} items</span>
            </div>
            {loc.items.map((item) => (
              <div
                key={item.identifier}
                className="expoams-history-card-detail"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>{item.identifier}</span>
                <span style={{ fontWeight: 500 }}>x{item.quantity}</span>
              </div>
            ))}
          </div>
        ))}

      {/* By Item */}
      {!loading &&
        viewMode === 'by-item' &&
        filteredItems.map((item) => (
          <div key={item.identifier} className="expoams-history-card">
            <div className="expoams-history-card-header">
              <span>{item.identifier}</span>
              <span style={{ fontWeight: 600 }}>Total: {item.totalQuantity}</span>
            </div>
            {item.locationBreakdown.map((lb) => (
              <div
                key={lb.location}
                className="expoams-history-card-detail"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>📍 {lb.location}</span>
                <span>x{lb.quantity}</span>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};

export default InventoryView;
